import React from 'react'
import { Rule, Tier } from 'types/progressiveDiscountTypes'
import { useProductProgressiveTiers } from 'hooks/useProductProgressiveTiers'
import { itemBenefitLabel, pureItemValueLabel, shippingBenefitLabel } from 'utils/progressiveDiscount'
import * as Style from './styles'

interface Props {
  productId: number | undefined
}

// Mesmo modelo de rotulo do card de grupo (buildBadgeText): 'both' junta item+frete;
// 'shipping' puro usa shippingBenefitLabel; item puro respeita discount_type.
function bestTierLabel(tier: Tier, rule: Rule | undefined, unitPrice: number | undefined): string {
  if (rule?.benefit_target === 'both') {
    const item = itemBenefitLabel(tier, rule.discount_type, unitPrice)
    const freight = shippingBenefitLabel(tier)
    return freight ? `${item} + ${freight}` : item
  }

  if (rule?.benefit_target === 'shipping') {
    return shippingBenefitLabel(tier) ?? `${tier.discount_percent}% OFF`
  }

  if (rule?.discount_type === 'value') {
    return pureItemValueLabel(tier, unitPrice)
  }

  return `até ${tier.discount_percent}% OFF`
}

/**
 * Badge discreto com a maior faixa de desconto progressivo do produto (item, frete ou ambos),
 * exibido por variacao na tela de checkout — mesmo modelo de badge das outras telas.
 */
export const ProductTiers = ({ productId }: Props) => {
  const { tiers, rule, unitPrice, hasDiscount, loading } = useProductProgressiveTiers(productId)

  if (!productId || loading || !hasDiscount || tiers.length === 0) return null

  const isShippingOnly = rule?.benefit_target === 'shipping'
  const displayable = [...tiers]
    .sort((a, b) => a.min_quantity - b.min_quantity)
    .filter(t => (isShippingOnly ? Number(t.shipping_value) > 0 : Number(t.discount_percent) > 0))

  if (displayable.length === 0) return null

  const last = displayable[displayable.length - 1]

  return <Style.Badge>{bestTierLabel(last, rule, unitPrice)}</Style.Badge>
}
